import React, { useMemo } from 'react';
import { FaStar } from 'react-icons/fa';
import './ReviewsShowcase.css';

const CRITERIA = [
  { key: 'quality', label: 'Calidad' },
  { key: 'price', label: 'Precio' },
  { key: 'punctuality', label: 'Puntualidad' },
];

const formatScore = value => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toFixed(1);
};

const formatDate = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const buildInitials = text => {
  if (!text) return '??';
  return text
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() || '')
    .join('') || '??';
};

const renderStars = (value, variant = 'small') => {
  const score = Number(value);
  const safeScore = Number.isFinite(score) ? Math.max(0, Math.min(5, score)) : 0;
  const filled = Math.round(safeScore);
  return Array.from({ length: 5 }).map((_, index) => (
    <FaStar
      key={`${variant}-${index}`}
      className={`reviews-showcase__star reviews-showcase__star--${variant}${
        index < filled ? ' is-filled' : ''
      }`}
      aria-hidden="true"
    />
  ));
};

function ReviewRow({ review }) {
  const initials = buildInitials(review.authorName || 'Cliente Flotte');
  return (
    <article className="reviews-showcase__item">
      <header className="reviews-showcase__item-head">
        <div className="reviews-showcase__item-left">
          <div className="reviews-showcase__avatar" aria-hidden="true">
            {initials}
          </div>
          <div>
            <p className="reviews-showcase__item-name">{review.authorName || 'Cliente Flotte'}</p>
            <p className="reviews-showcase__item-date">{formatDate(review.created)}</p>
          </div>
        </div>
        <div className="reviews-showcase__item-rating">{renderStars(review.overall, 'inline')}</div>
      </header>
      <p className="reviews-showcase__item-comment">
        {review.comment || 'El cliente no dejó comentarios.'}
      </p>
    </article>
  );
}

export default function ReviewsShowcase({
  title = 'Evaluaciones',
  reviews = [],
  loading = false,
  emptyMessage = 'Aún no hay reseñas registradas.',
  maxVisible = 3,
  containerClassName = '',
}) {
  const overallAverage = useMemo(() => {
    const valid = reviews
      .map(review => review?.overall)
      .filter(value => typeof value === 'number' && !Number.isNaN(value));
    if (!valid.length) return null;
    const sum = valid.reduce((acc, value) => acc + value, 0);
    return sum / valid.length;
  }, [reviews]);

  const breakdown = useMemo(() => {
    return CRITERIA.map(item => {
      const valid = reviews
        .map(review => review?.[item.key])
        .filter(value => typeof value === 'number' && !Number.isNaN(value));
      if (!valid.length) {
        return { ...item, value: null, percent: 0 };
      }
      const sum = valid.reduce((acc, value) => acc + value, 0);
      const score = sum / valid.length;
      return { ...item, value: score, percent: (Math.min(5, Math.max(0, score)) / 5) * 100 };
    });
  }, [reviews]);

  const visibleReviews = useMemo(
    () => reviews.slice(0, Math.max(0, maxVisible)),
    [reviews, maxVisible]
  );

  const hasMore = reviews.length > visibleReviews.length;

  return (
    <section className={`reviews-showcase ${containerClassName}`.trim()}>
      <header className="reviews-showcase__header">
        <h3>{title}</h3>
        <span className="reviews-showcase__badge">
          {reviews.length} {reviews.length === 1 ? 'reseña' : 'reseñas'}
        </span>
      </header>

      <div className="reviews-showcase__summary">
        <div className="reviews-showcase__aggregate">
          <p className="reviews-showcase__aggregate-score">{formatScore(overallAverage || 0)}</p>
          <div className="reviews-showcase__score-stars">
            {renderStars(overallAverage || 0, 'score')}
          </div>
          <p className="reviews-showcase__aggregate-label">
            Basado en {reviews.length || 0} {reviews.length === 1 ? 'reseña' : 'reseñas'}
          </p>
        </div>
        <div className="reviews-showcase__breakdown">
          {breakdown.map(item => (
            <div className="reviews-showcase__breakdown-row" key={item.key}>
              <span>{item.label}</span>
              <div className="reviews-showcase__progress">
                <span style={{ width: `${item.percent}%` }} />
              </div>
              <span className="reviews-showcase__breakdown-score">
                {item.value === null ? '—' : formatScore(item.value)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="reviews-showcase__meta">Cargando reseñas...</p>
      ) : visibleReviews.length ? (
        <div className="reviews-showcase__list">
          {visibleReviews.map(review => (
            <ReviewRow key={review.id} review={review} />
          ))}
        </div>
      ) : (
        <p className="reviews-showcase__empty">{emptyMessage}</p>
      )}

      {hasMore ? (
        <button type="button" className="reviews-showcase__ghost-btn">
          Ver las {reviews.length} reseñas
        </button>
      ) : null}
    </section>
  );
}
